import React, { useCallback, useRef, useState } from "react"
import { ReactComponent as UploadIcon } from "@ciq/shared/assets/icons/button/upload_icon.svg"
import { ACCEPT_STRING, getValidationError } from "../constants"

/**
 * @typedef {Object} FileDropZoneProps
 * @property {(files: File[]) => void} onFilesSelect - Callback when valid files are selected
 * @property {(error: string) => void} [onError] - Callback when file validation fails
 * @property {boolean} [disabled=false] - Boolean indicating if drop zone is disabled
 * @property {boolean} [collapsed=false] - Boolean indicating if drop zone renders as compact button
 * @property {string} [title] - Custom primary text
 * @property {string} [subtitle] - Custom secondary text
 */

/**
 * Drop zone component for floor plan file uploads.
 * Supports drag-and-drop and click-to-browse. Validates file type (PDF/PNG/JPG) and size (50MB).
 * Has two modes: full (large drop zone) and collapsed (compact upload button).
 *
 * @param {FileDropZoneProps} props - Component props
 * @returns {React.ReactElement} The drop zone component
 */
export function FileDropZone({
  onFilesSelect,
  onError,
  disabled = false,
  collapsed = false,
  title,
  subtitle,
}) {
  const inputRef = useRef(null)
  const [isDragging, setIsDragging] = useState(false)
  const dragCounterRef = useRef(0)

  const validateAndFilter = useCallback((fileList) => {
    const validFiles = []
    for (const file of Array.from(fileList)) {
      const error = getValidationError(file)
      if (error) {
        onError?.(error)
        continue
      }
      validFiles.push(file)
    }
    return validFiles
  }, [onError])

  const handleClick = useCallback(() => {
    if (!disabled && inputRef.current) {
      inputRef.current.click()
    }
  }, [disabled])

  const handleKeyDown = useCallback((event) => {
    if (disabled) return
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      inputRef.current?.click()
    }
  }, [disabled])

  const handleFileChange = useCallback((event) => {
    if (disabled) return
    const files = event.target.files
    if (!files || files.length === 0) return

    const fileArray = Array.from(files)
    event.target.value = ""

    const validFiles = validateAndFilter(fileArray)
    if (validFiles.length > 0) {
      onFilesSelect(validFiles)
    }
  }, [disabled, onFilesSelect, validateAndFilter])

  const handleDragEnter = useCallback((event) => {
    event.preventDefault()
    event.stopPropagation()
    dragCounterRef.current++
    if (event.dataTransfer.items && event.dataTransfer.items.length > 0) {
      setIsDragging(true)
    }
  }, [])

  const handleDragLeave = useCallback((event) => {
    event.preventDefault()
    event.stopPropagation()
    dragCounterRef.current--
    if (dragCounterRef.current === 0) {
      setIsDragging(false)
    }
  }, [])

  const handleDragOver = useCallback((event) => {
    event.preventDefault()
    event.stopPropagation()
  }, [])

  const handleDrop = useCallback((event) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)
    dragCounterRef.current = 0

    if (disabled) return

    const files = event.dataTransfer.files
    if (!files || files.length === 0) return

    const validFiles = validateAndFilter(files)
    if (validFiles.length > 0) {
      onFilesSelect(validFiles)
    }
  }, [disabled, onFilesSelect, validateAndFilter])

  const fileInput = (
    <input
      ref={inputRef}
      data-testid="file-input"
      type="file"
      accept={ACCEPT_STRING}
      multiple
      onChange={handleFileChange}
      style={{ display: "none" }}
      aria-hidden="true"
      tabIndex={-1}
    />
  )

  if (collapsed) {
    return (
      <div>
        {fileInput}
        <button
          type="button"
          className="file-upload-btn"
          onClick={handleClick}
          disabled={disabled}
        >
          <UploadIcon />
          Upload Files
        </button>
      </div>
    )
  }

  const className = [
    "file-upload-dropzone",
    disabled && "file-upload-dropzone--disabled",
    isDragging && !disabled && "file-upload-dropzone--dragging",
  ].filter(Boolean).join(" ")

  return (
    <div
      data-testid="file-drop-zone"
      className={className}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label="Drop zone for file upload. Drop files here or press Enter to browse."
      aria-disabled={disabled}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {fileInput}
      <div className="file-upload-dropzone__icon">
        <UploadIcon />
      </div>
      <div className="file-upload-dropzone__text">
        <span className="file-upload-dropzone__primary">
          {title || "Drop files or click to browse"}
        </span>
        <span className="file-upload-dropzone__secondary">
          {subtitle || "Accepts PDF, PNG, JPG up to 50 MB per file"}
        </span>
      </div>
    </div>
  )
}
